import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { CategoryCreateRequest } from '@/api/handlers/resources/categories/requests';
import type { CategoryWriteResponse } from '@/api/handlers/resources/categories/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { CategoryQueryKeys } from './categoryQueryKeys';

/**
 * Mutation behind the Add Category submit. The bos-api takes the cropper
 * payload server-side (multipart) so the round-trip is a single POST instead
 * of POST images + POST categories + PUT categories.
 *
 * The management table reads the nested `/tree` and the server owns the
 * placement under the parent, so on success we invalidate every category query
 * (tree, details, and the picker) and let them refetch — no optimistic insert.
 */
export const useCreateCategoryMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CategoryWriteResponse>,
        Error,
        CategoryCreateRequest
    >({
        mutationFn: (body) => apiClient.categories.createCategory(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: CategoryQueryKeys.all,
                });
            }
        },
    });
};
